import type { TaskAttention } from "../persistence/records"
import type { ProjectColor } from "../projectAppearance"
import type { PersonLevel, PersonRecord } from "./people"
import type { ComputerOverview, ComputerSummary } from "./types"

/** Where a project lives, as the sidebar tags it. */
export type ProjectSource =
    | { kind: "local" }
    | { kind: "own"; nodeId: string; computerName: string }
    | { kind: "shared"; nodeId: string; computerName: string; personName: string; glyph?: string; color?: string; level: PersonLevel }

export interface RemoteFleetProject {
    id: string
    nodeId: string
    name: string
    color?: ProjectColor
    glyph?: string
    projectChars?: string
    projectEmoji?: string
    source: ProjectSource
    status: ComputerOverview["status"]
    refreshedAt: string
}

export interface RemoteFleetTask {
    attention?: TaskAttention
    sleeping?: boolean
    taskId: string
    workspaceId: string
    nodeId: string
    title: string
    lastActivityAt: string
    running: boolean
    unread: boolean
}

/** "Parhams-MacBook-Air.local" reads as "Parhams-MacBook-Air" in a 12-character tag. */
export function shortComputerName(name: string): string {
    const trimmed = name.replace(/\.local$/i, "").trim()
    return trimmed.length > 14 ? `${trimmed.slice(0, 13)}…` : trimmed
}

export function projectSourceFor(computer: ComputerSummary, people: readonly PersonRecord[]): ProjectSource {
    if (computer.kind === "local") return { kind: "local" }
    const person = people.find((candidate) => candidate.nodeId === computer.nodeId)
    if (person) {
        return {
            kind: "shared",
            nodeId: computer.nodeId,
            computerName: computer.name,
            personName: person.personName,
            ...(person.glyph ? { glyph: person.glyph } : {}),
            ...(person.color ? { color: person.color } : {}),
            level: person.level,
        }
    }
    return { kind: "own", nodeId: computer.nodeId, computerName: computer.name }
}

export function sourceLabel(source: ProjectSource): string {
    switch (source.kind) {
        case "local":
            return "On this computer"
        case "own":
            return `On ${source.computerName}`
        case "shared":
            return `Shared by ${source.personName} · ${source.level === "none" ? "no access yet" : source.level} · ${source.computerName}`
    }
}

/**
 * Projects on every registered computer except the active one, in registry
 * order, from each computer's bounded overview. Archived projects stay out:
 * a remote archive is that computer's business, not a row here.
 */
export function remoteFleetProjects({
    computers,
    people,
    overviews,
    activeNodeId,
}: {
    computers: readonly ComputerSummary[]
    people: readonly PersonRecord[]
    overviews: Readonly<Record<string, ComputerOverview>>
    activeNodeId: string
}): RemoteFleetProject[] {
    const projects: RemoteFleetProject[] = []
    for (const computer of computers) {
        if (computer.nodeId === activeNodeId) continue
        const overview = overviews[computer.nodeId]
        if (!overview) continue
        const source = projectSourceFor(computer, people)
        for (const project of overview.projects) {
            if (project.archived) continue
            projects.push({
                id: project.id,
                nodeId: computer.nodeId,
                name: project.name,
                ...(project.color ? { color: project.color } : {}),
                ...(project.glyph ? { glyph: project.glyph } : {}),
                ...(project.projectChars ? { projectChars: project.projectChars } : {}),
                ...(project.projectEmoji ? { projectEmoji: project.projectEmoji } : {}),
                source,
                status: overview.status,
                refreshedAt: overview.refreshedAt,
            })
        }
    }
    return projects
}

export function remoteFleetTasks(overview: ComputerOverview | undefined, workspaceId: string): RemoteFleetTask[] {
    if (!overview) return []
    return overview.recentTasks
        .filter((task) => task.workspaceId === workspaceId)
        .map((task) => ({
            taskId: task.taskId,
            workspaceId: task.workspaceId,
            nodeId: overview.nodeId,
            title: task.title,
            lastActivityAt: task.lastActivityAt,
            running: task.running,
            unread: task.unread,
            attention: task.attention,
            sleeping: task.sleeping,
        }))
}
